import * as PIXI from 'pixi.js-legacy';

export interface ICompass {
    x: number | any;
    y: number | any;
    yaw?: number | any;
}

export interface IPoint {
    x: number | any;
    y: number | any;
}
export interface ICircleData {
    pos: {
        x: number | any;
        y: number | any;
        yaw?: number | any;
    },
    color: string | any,

}

import compassIcon from '@assets/images/trainUavIcon1.png';

export abstract class BaseDrawer {
    protected containerRef: HTMLDivElement | null = null;
    protected containerWidth = 0;
    protected containerHeight = 0;
    // 首次挂载的飞行画布作为逻辑坐标系。后续切换容器时只等比缩放、居中，
    // 避免因容器宽高比变化导致电子桩、轨迹和偏差点横纵方向缩放不一致。
    protected logicalWidth = 0;
    protected logicalHeight = 0;
    protected viewportScale = 1;
    protected viewportOffsetX = 0;
    protected viewportOffsetY = 0;
    protected pixiApp: PIXI.Application | null = null;
    protected compass: PIXI.Sprite | null = null;
    protected lastYaw: number | null = null;
    protected compassPos: ICompass = { x: 0, y: 0, yaw: 0 };
    protected useCompass = false;
    protected trailGraphics: PIXI.Graphics | null = null;
    protected circlesContainer: PIXI.Container | null = null;
    protected circleNodes: PIXI.Graphics[] = [];
    protected circleDatas: ICircleData[] = [];
    protected trailPoints: ICompass[] = [];
    protected lastPoint: IPoint = { x: 0, y: 0 };
    protected trailWidth = 1;
    protected userTrail = true;
    protected preserveDrawingBuffer = false;
    protected preserveViewportAspectRatio = false;


    constructor(
        containerRef?: HTMLDivElement,
        compassPos?: ICompass,
        userTrail?: boolean,
        preserveDrawingBuffer?: boolean,
        preserveViewportAspectRatio?: boolean,
    ) {
        this.preserveDrawingBuffer = preserveDrawingBuffer !== undefined?preserveDrawingBuffer:false;
        this.preserveViewportAspectRatio = preserveViewportAspectRatio ?? false;
        if (containerRef) {
            this.containerRef = containerRef;
            this.userTrail = userTrail !== undefined?userTrail:false;
            this.initContainer();
            this.initPixiApp();
            if (compassPos) {
                this.compassPos = compassPos;
                this.useCompass = true;
                this.drawCompass(true);
            }
        }
    }

    protected initContainer(width?: number, height?: number) {
        if (!this.containerRef) return;
        const rect = this.containerRef.getBoundingClientRect();
        this.containerWidth = width !== undefined ? width : rect.width;
        this.containerHeight = height !== undefined ? height : rect.height;

        if ((!this.preserveViewportAspectRatio || !this.logicalWidth) && this.containerWidth > 0) {
            this.logicalWidth = this.containerWidth;
        }
        if ((!this.preserveViewportAspectRatio || !this.logicalHeight) && this.containerHeight > 0) {
            this.logicalHeight = this.containerHeight;
        }

        this.updateViewport();
    }

    protected updateViewport() {
        if (!this.logicalWidth || !this.logicalHeight) {
            this.viewportScale = 1;
            this.viewportOffsetX = 0;
            this.viewportOffsetY = 0;
            return;
        }

        this.viewportScale = Math.min(
            this.containerWidth / this.logicalWidth,
            this.containerHeight / this.logicalHeight,
        );
        this.viewportOffsetX = (this.containerWidth - this.logicalWidth * this.viewportScale) / 2;
        this.viewportOffsetY = (this.containerHeight - this.logicalHeight * this.viewportScale) / 2;
    }

    protected get viewportWidth() {
        return this.logicalWidth * this.viewportScale;
    }

    protected get viewportHeight() {
        return this.logicalHeight * this.viewportScale;
    }

    protected getCanvasPoint(pos: IPoint): IPoint {
        return {
            x: this.viewportOffsetX + pos.x * this.viewportWidth,
            y: this.viewportOffsetY + pos.y * this.viewportHeight,
        };
    }

    protected getBoundedCanvasPoint(pos: IPoint, halfWidth = 0, halfHeight = 0): IPoint {
        const point = this.getCanvasPoint(pos);
        return {
            x: Math.max(
                this.viewportOffsetX + halfWidth,
                Math.min(point.x, this.viewportOffsetX + this.viewportWidth - halfWidth),
            ),
            y: Math.max(
                this.viewportOffsetY + halfHeight,
                Math.min(point.y, this.viewportOffsetY + this.viewportHeight - halfHeight),
            ),
        };
    }

    protected initPixiApp() {
        if (!this.containerRef) return;
        this.pixiApp = new PIXI.Application({
            width: this.containerWidth,
            height: this.containerHeight,
            transparent: true,
            resolution: window.devicePixelRatio || 2,
            autoDensity: true,
            forceCanvas:true,
            preserveDrawingBuffer: this.preserveDrawingBuffer,
        })
        this.pixiApp.stage.sortableChildren = true;
        this.containerRef.appendChild(this.pixiApp.view);
    }

    //绘制指南针
    protected async drawCompass(isInit?: boolean) {
        if (!this.pixiApp) return;
        this.compass = null;
        const texture = await PIXI.Texture.from(compassIcon);
        const sprite = new PIXI.Sprite(texture);
        const wh = this.viewportWidth * (24 / 400);
        sprite.width = wh;
        sprite.height = wh;
        sprite.visible = false;
        sprite.anchor.set(0.5, 0.5);

        this.compass = sprite;
        if (this.lastYaw !== null) {
            this.compass.rotation = this.lastYaw * (Math.PI / 180);
        }
        // 使用 zIndex 确保指南针始终渲染在最上层
        sprite.zIndex = 9999;
        this.pixiApp.stage.addChild(sprite);
        this.setCompassPos({ x: this.compassPos.x, y: this.compassPos.y });
        this.compass.rotation = this.compassPos.yaw * (Math.PI / 180);
        if (!this.compass.visible) {
            this.compass.visible = true;
        }
    }


    //设置指南针位置
    protected setCompassPos(pos: IPoint, isReset?: boolean) {
        const halfWidth = this.compass ? this.compass.width / 2 : 0;
        const halfHeight = this.compass ? this.compass.height / 2 : 0;
        const point = isReset
            ? this.getCanvasPoint(pos)
            : this.getBoundedCanvasPoint(pos, halfWidth, halfHeight);

        if (this.compass) {
            this.compass.x = point.x;
            this.compass.y = point.y;
        }
    }

    // 在 BaseDrawer 类中添加方法，更新初始指南针位置
    public updateInitialCompassPos(pos: ICompass) {
        this.compassPos = { ...pos };
        // 如果当前指南针存在，立即更新位置
        if (this.compass) {
            this.setCompassPos({ x: pos.x, y: pos.y });
            this.compass.rotation = pos.yaw * (Math.PI / 180);
        }
    }

    //更新指南针
    public updateCompass(compassData: ICompass) {
        if (!this.compass) return;
        const { x, y, yaw } = compassData;
        const point = this.getBoundedCanvasPoint({ x, y });

        if (this.lastYaw !== yaw) {
            this.compass.rotation = yaw * (Math.PI / 180);
            this.lastYaw = yaw;
        }

        if (this.trailPoints.length > 1 && this.lastPoint.x === point.x && this.lastPoint.y === point.y) return;
        this.lastPoint = { x: point.x, y: point.y };
        this.trailPoints.push({ x, y, yaw });

        this.setCompassPos({ x, y });
        if (!this.compass.visible) {
            this.compass.visible = true;
        }

        if (this.trailPoints.length > 1 && this.userTrail) {
            this.drawTrail();
        }
    }

    //绘制轨迹
    protected drawTrail() {
        if (!this.trailGraphics) {
            const graphic = new PIXI.Graphics();
            this.trailGraphics = graphic;
            this.pixiApp?.stage.addChild(graphic);
            this.trailWidth = (1 / 400) * this.viewportWidth;
        }

        if (this.trailGraphics) {
            this.pixiApp?.stage.setChildIndex(this.trailGraphics, this.pixiApp.stage.children.length - 2);
            this.trailGraphics.clear();
            this.trailGraphics.lineStyle(this.trailWidth, 0xff0000, 1);
            const firstPoint = this.trailPoints[0];

            const halfWidth = this.compass ? this.compass.width / 2 : 0;
            const halfHeight = this.compass ? this.compass.height / 2 : 0;
            const firstCanvasPoint = this.getBoundedCanvasPoint(firstPoint, halfWidth, halfHeight);

            this.trailGraphics.moveTo(firstCanvasPoint.x, firstCanvasPoint.y);

            this.trailPoints.forEach((point: any) => {
                if (this.trailGraphics) {
                    const canvasPoint = this.getBoundedCanvasPoint(point, halfWidth, halfHeight);
                    this.trailGraphics.lineTo(canvasPoint.x, canvasPoint.y);
                }
            });

            if (this.trailGraphics.geometry) {
                this.trailGraphics.geometry.invalidate();
            }
        }
    }

    //重置轨迹
    public resetTrailPoints(list:any[]) {
        if (this.trailGraphics) {
            this.resetTrail();
        }
        this.trailPoints = list;
        this.drawTrail();
        let lastPoint = this.trailPoints[this.trailPoints.length - 1];
        if(this.compass){
            this.compass.rotation = lastPoint.yaw * (Math.PI / 180);
            this.setCompassPos({x:lastPoint.x,y:lastPoint.y});
        }
        // this.lastPoint = { x: lastPoint.x, y: lastPoint.y };
    }

    // 重置偏差点
    public resetCircles(list:any[]) {
        if (this.circlesContainer) {
            // 清理
            this.clearCircles();
            // 从父容器中移除
            if (this.circlesContainer) {
                this.circlesContainer.parent.removeChild(this.circlesContainer);
            }
            this.circlesContainer = null;
        }
        list.forEach(item=>{
            this.drawCircle(item.mapPosition,item.color);
        })
    }

    protected resetTrail() {
        this.trailPoints = [];
        // 完全销毁并重新创建 Graphics 对象
        if (this.trailGraphics) {
            // 从父容器中移除
            if (this.trailGraphics.parent) {
                this.trailGraphics.parent.removeChild(this.trailGraphics);
            }
            // 彻底销毁
            this.trailGraphics.destroy();
        }
        this.trailGraphics = null;
        // 创建新的 Graphics 对象
        this.trailGraphics = new PIXI.Graphics();
        this.pixiApp?.stage.addChild(this.trailGraphics);
    }

    protected parsePixiColor(color: number | string): number {
        if (typeof color === 'number') return color;
        const normalized = color.trim().toLowerCase();
        if (normalized.startsWith('#')) {
            const hex = normalized.slice(1);
            const fullHex =
                hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
            const parsed = Number.parseInt(fullHex, 16);
            return Number.isFinite(parsed) ? parsed : 0xffffff;
        }
        if (normalized.startsWith('0x')) {
            const parsed = Number.parseInt(normalized.slice(2), 16);
            return Number.isFinite(parsed) ? parsed : 0xffffff;
        }
        return 0xffffff;
    }

    protected ensureCirclesContainer() {
        if (!this.pixiApp) return;
        if (this.circlesContainer) return;
        this.circlesContainer = new PIXI.Container();
        this.pixiApp.stage.addChild(this.circlesContainer);
    }

    public clearCircles() {
        if (!this.circlesContainer) {
            this.circleNodes = [];
            this.circleDatas = [];
            return;
        }
        for (const node of this.circleNodes) {
            node.destroy();
        }
        this.circleNodes = [];
        this.circleDatas = [];
        this.circlesContainer.removeChildren();
    }

    // 画圆：pos 为 0~1 归一化坐标（与 updateCompass 一致），每次调用都会创建新的圆节点
    public drawCircle(pos: IPoint, color: number | string) {
        if (!this.pixiApp) return;
        this.ensureCirclesContainer();
        if (!this.circlesContainer) return;

        const radius = (6 / 400) * this.viewportWidth;
        const halfWidth = this.compass ? this.compass.width / 2 : 0;
        const halfHeight = this.compass ? this.compass.height / 2 : 0;
        const point = this.getBoundedCanvasPoint(pos, halfWidth, halfHeight);

        const pixiColor = this.parsePixiColor(color);

        const g = new PIXI.Graphics();
        g.beginFill(pixiColor, 1);
        g.drawCircle(point.x, point.y, radius);
        g.endFill();
        this.circlesContainer.addChild(g);
        this.circleNodes.push(g);
        this.circleDatas.push(<ICircleData>{pos, color});
    }


    // 初始化画布
    protected renderResize() {
        if(!this.containerRef || !this.pixiApp) return
        this.pixiApp.renderer.resize(this.containerWidth, this.containerHeight);
        this.pixiApp.stage.children.forEach((child: any) => {
            if (child instanceof PIXI.Graphics) {
                child.clear(); // 清空图形
            }
        });
        this.pixiApp.stage.removeChildren();
    }


    protected clearStage() {
        if (!this.pixiApp) return;
        for (let i = this.pixiApp.stage.children.length - 1; i >= 0; i--) {
            const child = this.pixiApp.stage.children[i];
            child.destroy();
            this.pixiApp.stage.removeChild(child);
        }
    }

    public destroy() {
        this.clearStage();
        if (this.pixiApp) {
            this.pixiApp.destroy(true);
            this.pixiApp = null;
        }
        if (this.containerRef) {
            this.containerRef = null;
        }
    }

    public reset(compassPas = null){
        
        if(this.compass && this.trailPoints.length > 0){
            this.lastPoint = {x:0,y:0}; // 清空容器引用
            this.lastYaw = null; // 清空容器引用
        }
        setTimeout(() => {
            this.setCompassPos({x:this.compassPos.x,y:this.compassPos.y}, true)
            if(this.compass){
                let yaw;
                if(compassPas === null){
                    yaw = this.compassPos.yaw 
                }else{
                    yaw = compassPas
                }
                this.compass.rotation = yaw * (Math.PI / 180);
            }
        }, 100);
        this.trailGraphics?.clear();
        this.resetTrail();
        this.clearCircles();
    }

    abstract resizeHandler(isFlytoPage?:boolean): void;

    public exportToImage(): string | void {
        if (!this.pixiApp) return;
        // 强制渲染当前帧，确保缓冲区内容最新
        this.pixiApp.renderer.render(this.pixiApp.stage);
        return this.pixiApp.view.toDataURL();
    }
}
